/**
 * Centralized SEO Content & Writing Rules for BizNearYou
 * Enforces editorial quality, tone guidelines, and prevents "AI slop" or deceptive marketing.
 */

export interface ContentRuleViolation {
  ruleId: string;
  severity: "error" | "warning";
  message: string;
  matchedText?: string;
}

export const PROHIBITED_BUZZWORDS = [
  "revolutionize",
  "game-changing",
  "seamless",
  "unlock your potential",
  "leverage the power",
  "cutting-edge",
  "navigate the landscape",
  "in today's fast-paced world",
  "powerhouse",
  "synergy",
  "paradigm shift",
  "supercharge",
  "empower your job search",
  "guaranteed job",
  "guaranteed hire",
  "instant job offer",
  "secret job hack",
];

export const SEO_CONTENT_RULES = {
  prohibitedBuzzwords: PROHIBITED_BUZZWORDS,
  hiringClaimRequirements: {
    mustQualifyHiringClaims: true,
    requiredQualifiers: ["may be hiring", "showing growth signals", "possible opportunity", "hiring indicators"],
    forbiddenAbsoluteClaims: ["is currently hiring", "has open jobs right now", "guaranteed job", "hiring immediately"],
  },
  localContextRequirements: {
    minUniqueWordCount: 150,
    minLocalSignalReferences: 1,
    prohibitIdenticalCityTemplates: true,
  },
  metadataLengthLimits: {
    title: { min: 20, max: 70 },
    description: { min: 60, max: 170 },
  },
};

/**
 * Validates any string or page content against editorial and content quality rules.
 */
export function validateSeoContentRules(text: string): { isValid: boolean; violations: ContentRuleViolation[] } {
  const violations: ContentRuleViolation[] = [];
  if (!text) {
    return { isValid: true, violations: [] };
  }

  const lowerText = text.toLowerCase();

  // 1. Check for prohibited buzzwords
  for (const word of PROHIBITED_BUZZWORDS) {
    if (lowerText.includes(word.toLowerCase())) {
      violations.push({
        ruleId: "PROHIBITED_BUZZWORD",
        severity: "error",
        message: `Contains prohibited marketing cliché or buzzword: "${word}"`,
        matchedText: word,
      });
    }
  }

  // 2. Check for absolute unverified hiring claims
  for (const absoluteClaim of SEO_CONTENT_RULES.hiringClaimRequirements.forbiddenAbsoluteClaims) {
    if (lowerText.includes(absoluteClaim.toLowerCase())) {
      violations.push({
        ruleId: "UNQUALIFIED_HIRING_CLAIM",
        severity: "error",
        message: `Contains unverified absolute hiring claim: "${absoluteClaim}". Claims must be qualified with uncertainty (e.g., "may be hiring").`,
        matchedText: absoluteClaim,
      });
    }
  }

  return {
    isValid: violations.filter((v) => v.severity === "error").length === 0,
    violations,
  };
}
